import React, { useState, useEffect } from "react";
import { useCart } from "@/src/lib/CartContext";
import { useAuth } from "@/src/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Trash2, Plus, Minus, MessageCircle, ShoppingBag, ArrowRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { collection, doc, getDoc, addDoc, serverTimestamp, updateDoc, increment } from "firebase/firestore";
import { db as firestoreDb } from "@/src/lib/firebase";
import { getWhatsAppLink, shortenUrl } from "@/src/lib/utils";
import { toast } from "sonner";
import { Shop, Order } from "@/src/types";

interface GroupedCart {
  [shopId: string]: {
    shop: Shop | null;
    items: any[];
  };
}

export function CartPage() {
  const { cart, removeFromCart, updateQuantity, clearCart, loading: cartLoading } = useCart();
  const { user, isAuthReady } = useAuth();
  const navigate = useNavigate();
  const [groupedCart, setGroupedCart] = useState<GroupedCart>({});
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<{[shopId: string]: any}>({});

  useEffect(() => {
    if (isAuthReady && !user) {
      navigate("/login");
    }
  }, [user, isAuthReady, navigate]);

  useEffect(() => {
    const groupItems = async () => {
      if (cart.length === 0) {
        setGroupedCart({});
        setLoading(false);
        return;
      }

      const groups: GroupedCart = {};
      const shopIds = Array.from(new Set(cart.map(item => item.shopId)));

      for (const shopId of shopIds) {
        try {
          const shopDoc = await getDoc(doc(firestoreDb, "shops", shopId as string));
          groups[shopId as string] = {
            shop: shopDoc.exists() ? { ...shopDoc.data(), id: shopDoc.id } as Shop : null,
            items: cart.filter(item => item.shopId === shopId)
          };
        } catch (error) {
          console.error(`Error fetching shop ${shopId}:`, error);
          groups[shopId as string] = {
            shop: null,
            items: cart.filter(item => item.shopId === shopId)
          };
        }
      }
      setGroupedCart(groups);
      setLoading(false);
    };

    groupItems();
  }, [cart]);

  const handleSendWhatsApp = async (shopId: string) => {
    const group = groupedCart[shopId];
    if (!group || !group.shop || !group.shop.whatsapp || !user) {
      toast.error("Esta tienda no tiene WhatsApp configurado");
      return;
    }

    setIsProcessing(true);
    try {
      const shop = group.shop;
      const items = group.items;
      
      let message = `*Nuevo Pedido de EmpreMarket*\n\n`;
      let total = 0;

      for (const item of items) {
        const subtotal = item.price * item.quantity;
        total += subtotal;
        message += `• ${item.name} x${item.quantity} - $${subtotal.toLocaleString()}\n`;
      }

      const paymentMethod = selectedPaymentMethods[shopId] || "transfer";
      message += `\n*Método de Pago:* ${paymentMethod.toUpperCase()}`;
      message += `\n*Total: $${total.toLocaleString()}*`;
      message += `\n\n*Pedido realizado por:* ${user?.displayName || "Usuario"}`;
      message += `\n*Email:* ${user?.email}`;

      // Save order to Firestore
      const orderData = {
        buyerUid: user.uid,
        items: items.map(item => ({
          productId: item.productId,
          shopId: item.shopId,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          image: item.image
        })),
        total: total,
        status: "pending",
        paymentMethod: paymentMethod, 
        createdAt: new Date().toISOString(),
        shopId: shopId // Added for easier filtering
      };

      await addDoc(collection(firestoreDb, "orders"), orderData);
      
      // LOYALTY POINTS LOGIC
      if (shop.isClubAffiliated) {
        const pointsToGain = Math.floor(total / 100);
        if (pointsToGain > 0) {
          const cardRef = doc(firestoreDb, "mall_club", user.uid);
          const cardSnap = await getDoc(cardRef);
          
          if (cardSnap.exists()) {
            await updateDoc(cardRef, {
              points: increment(pointsToGain)
            });
            toast.success(`¡Has ganado ${pointsToGain} puntos por tu compra en una tienda Club!`);
          }
        }
      }

      // Open WhatsApp
      window.open(getWhatsAppLink(shop.whatsapp, message), "_blank");
      
      toast.success("Pedido registrado y enviado a WhatsApp");
      
      // Remove these items from cart
      for (const item of items) {
        await removeFromCart(item.productId);
      }
    } catch (error) {
      console.error("Error processing order:", error);
      toast.error("Error al procesar el pedido");
    } finally {
      setIsProcessing(false);
    }
  };

  if (cartLoading || loading) {
    return (
      <div className="container mx-auto px-4 py-12 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <div className="max-w-md mx-auto">
          <ShoppingBag className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">Tu carrito está vacío</h2>
          <p className="text-muted-foreground mb-6">
            Parece que aún no has agregado nada a tu carrito. ¡Explora nuestras tiendas y encuentra algo increíble!
          </p>
          <Button asChild>
            <Link to="/shops">Explorar Tiendas</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Mi Carrito</h1>
      
      <div className="grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-8">
          {Object.entries(groupedCart).map(([shopId, group]: [string, any]) => (
            <Card key={shopId} className="overflow-hidden">
              <CardHeader className="bg-muted/50 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ShoppingBag className="h-5 w-5 text-primary" />
                    <CardTitle className="text-lg">
                      {group.shop ? group.shop.name : "Tienda Desconocida"}
                    </CardTitle>
                  </div>
                  {group.shop?.whatsapp && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-[#25D366] hover:text-[#128C7E] hover:bg-[#25D366]/10 gap-2"
                      onClick={() => handleSendWhatsApp(shopId)}
                      disabled={isProcessing}
                    >
                      <MessageCircle className="h-4 w-4" />
                      {isProcessing ? "Procesando..." : "Pedir por WhatsApp"}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {group.items.map((item: any) => (
                    <div key={item.productId} className="p-4 flex gap-4">
                      <div className="h-20 w-20 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                        <img 
                          src={item.image || `https://picsum.photos/seed/${item.productId}/200/200`} 
                          alt={item.name}
                          className="h-full w-full object-cover"
                          referrerPolicy="no-referrer"
                        />
                      </div>
                      <div className="flex-grow">
                        <div className="flex justify-between mb-1">
                          <h4 className="font-semibold">{item.name}</h4>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => removeFromCart(item.productId)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="text-sm font-bold text-primary">
                            ${item.price.toLocaleString()}
                          </div>
                          <div className="flex items-center gap-2 border rounded-md p-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-6 w-6"
                              onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                              disabled={item.quantity <= 1}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="text-sm font-medium w-4 text-center">{item.quantity}</span>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-6 w-6"
                              onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
              <CardFooter className="bg-muted/30 p-4 flex flex-col gap-4">
                <div className="w-full space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Seleccionar Método de Pago</p>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <Button 
                      variant={selectedPaymentMethods[shopId] === "transfer" || !selectedPaymentMethods[shopId] ? "default" : "outline"}
                      size="sm"
                      className="text-[10px] h-8"
                      onClick={() => setSelectedPaymentMethods({...selectedPaymentMethods, [shopId]: "transfer"})}
                    >
                      Transferencia
                    </Button>
                    {group.shop?.paymentSettings?.zelle?.active && (
                      <Button 
                        variant={selectedPaymentMethods[shopId] === "zelle" ? "default" : "outline"}
                        size="sm"
                        className="text-[10px] h-8"
                        onClick={() => setSelectedPaymentMethods({...selectedPaymentMethods, [shopId]: "zelle"})}
                      >
                        Zelle
                      </Button>
                    )}
                    {group.shop?.paymentSettings?.bizum?.active && (
                      <Button 
                        variant={selectedPaymentMethods[shopId] === "bizum" ? "default" : "outline"}
                        size="sm"
                        className="text-[10px] h-8"
                        onClick={() => setSelectedPaymentMethods({...selectedPaymentMethods, [shopId]: "bizum"})}
                      >
                        Bizum
                      </Button>
                    )}
                    {(group.shop?.binanceSettings?.active || group.shop?.paymentSettings?.binance?.active) && (
                      <Button 
                        variant={selectedPaymentMethods[shopId] === "binance" ? "default" : "outline"}
                        size="sm"
                        className="text-[10px] h-8"
                        onClick={() => setSelectedPaymentMethods({...selectedPaymentMethods, [shopId]: "binance"})}
                      >
                        Binance Pay
                      </Button>
                    )}
                    {group.shop?.paymentSettings?.bank?.active && (
                      <Button 
                        variant={selectedPaymentMethods[shopId] === "bank" ? "default" : "outline"}
                        size="sm"
                        className="text-[10px] h-8"
                        onClick={() => setSelectedPaymentMethods({...selectedPaymentMethods, [shopId]: "bank"})}
                      >
                        Depósito
                      </Button>
                    )}
                  </div>
                </div>

                <div className="flex justify-between items-center w-full pt-2 border-t">
                  <div className="text-sm text-muted-foreground">
                    {group.items.length} {group.items.length === 1 ? "producto" : "productos"}
                  </div>
                  <div className="font-bold">
                    Subtotal: ${group.items.reduce((acc: number, item: any) => acc + (item.price * item.quantity), 0).toLocaleString()}
                  </div>
                </div>
              </CardFooter>
            </Card>
          ))}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Resumen del Pedido</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between text-sm">
                <span>Total de productos</span>
                <span>{cart.reduce((acc, item) => acc + item.quantity, 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Tiendas diferentes</span>
                <span>{Object.keys(groupedCart).length}</span>
              </div>
              <div className="border-t pt-4 flex justify-between font-bold text-lg">
                <span>Total General</span>
                <span>${cart.reduce((acc, item) => acc + (item.price * item.quantity), 0).toLocaleString()}</span>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col gap-3">
              <p className="text-xs text-muted-foreground text-center">
                Los pedidos se envían individualmente a cada tienda por WhatsApp.
              </p>
              <Button variant="outline" className="w-full" onClick={clearCart}>
                Vaciar Carrito
              </Button>
            </CardFooter>
          </Card>
          
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <h4 className="font-semibold text-primary mb-2 flex items-center gap-2">
              <MessageCircle className="h-4 w-4" />
              ¿Cómo funciona?
            </h4>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Al hacer clic en "Pedir por WhatsApp" en cada tienda, se abrirá un chat con el vendedor con el detalle de los productos que elegiste de su tienda.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
